import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EstadoSede, Rubro } from '../models/marketplace.models';

const BASE = `${environment.apiNest}/marketplace`;

export interface MarcaResumen {
  idEmpresa: number;
  slug: string;
  rubro: Rubro;
  nombre: string;
  descripcion: string;
  rating: number;
  resenas: number;
  sedes: number; // conteo
  desde: number; // precio mínimo
  destacado: boolean;
  logoUrl?: string | null; // opcional: puede no venir en el listado
  portadaUrl?: string | null;
  /** Acento del negocio (#RRGGBB). Ver colorDe. */
  color?: string | null;
}

export interface Slot {
  inicio: string;
  fin: string;
}

/** Redes sociales de la marca (cada una string|null). */
export interface MarcaRedes {
  instagram: string | null;
  facebook: string | null;
  tiktok: string | null;
  web: string | null;
}

/** Sede con datos de contacto/ubicación para el MVP WhatsApp + Maps. */
export interface SedeDetalle {
  id: number;
  slug: string;
  nombre: string;
  direccion: string | null;
  distrito?: string | null;
  horario?: string | null;
  profesionalId?: number | null;
  whatsapp: string | null;
  lat: number | null;
  lng: number | null;
  mapsUrl: string | null;
  /** Servicios ofrecidos EN esta sede (puede diferir del catálogo completo de la marca). */
  servicios?: ServicioDetalle[];
  /**
   * Estado operativo de la sede, embebido por el backend en cada sede de getMarca(slug).
   * Opcional a propósito: si el backend todavía no lo expone llega `undefined` y la UI
   * simplemente no pinta el chip.
   */
  estado?: EstadoSede;
  [k: string]: any;
}

/** Una sede del buscador por cercanía (GET /marketplace/sedes/cercanas). */
export interface SedeCercana {
  idSede: number;
  sedeSlug: string;
  sede: string;
  direccion: string | null;
  marcaSlug: string;
  marcaNombre: string;
  rubro: string;
  lat: number | null;
  lng: number | null;
  /** Link de Google Maps que cargó el negocio ("Cómo llegar"). */
  mapsUrl: string | null;
  /** Precio mínimo; si se buscó un servicio, el de ese servicio. */
  desde: number | null;
  /** El servicio que coincidió con la búsqueda, si hubo búsqueda. */
  servicio: string | null;
  /** null si no se mandó ubicación o la sede no tiene coordenadas. */
  distanciaKm: number | null;
}

/** Detalle de marca que devuelve getMarca(slug). */
/** Un servicio de la marca (con foto opcional para el thumbnail). */
export interface ServicioDetalle {
  id: number;
  nombre: string;
  descripcion?: string | null;
  duracionMin?: number;
  precioBruto?: number;
  adelantoPct?: number;
  fotoUrl?: string | null;
  [k: string]: any;
}

export interface MarcaDetalle {
  idEmpresa?: number;
  slug: string;
  nombre: string;
  rubro: Rubro;
  descripcion?: string;
  rating?: number;
  resenas?: number;
  redes?: MarcaRedes | null;
  logoUrl?: string | null;
  portadaUrl?: string | null;
  /** Acento del negocio (#RRGGBB). Ver colorDe. */
  color?: string | null;
  sedes: SedeDetalle[];
  servicios: ServicioDetalle[];
  profesionalId?: number | null;
  [k: string]: any;
}

/** Una cita del cliente en la línea de tiempo unificada (marketplace + clínica). SPEC-026 Fase 2. */
export interface MiCita {
  origen: 'marketplace' | 'clinica';
  idRef: number;
  fecha: string;
  hora: string | null;
  sede: string | null;
  servicio: string | null;
  estado: string | null;
  /** Contexto de negocio: una cita de spa se presenta distinta que una de manicura. */
  empresa: string | null;
  slug: string | null;
  rubro: string | null;
}

export interface MisCitasResponse {
  items: MiCita[];
}

/** Recomendación dejada por la especialista (EvolucionTratamientoZona). SPEC-026 Fase 3. */
export interface MiRecomendacion {
  fecha: string | null;
  zona: string | null;
  sesion: number | null;
  comentarioCliente: string | null;
  comentario: string | null;
  /** Contexto de negocio del que proviene la recomendación. */
  empresa: string | null;
  rubro: string | null;
}

export interface MisRecomendacionesResponse {
  items: MiRecomendacion[];
}

/** Un movimiento del saldo de puntos del cliente. SPEC-026 Fase 4. */
export interface MovimientoPunto {
  fecha: string;
  origen: 'CITA' | 'RESERVA' | 'RECARGA' | 'CANJE' | 'AJUSTE';
  puntos: number;
  concepto: string | null;
}

export interface PuntosResumen {
  saldo: number;
  movimientos: MovimientoPunto[];
}

/** Un premio canjeable por puntos. SPEC-026 Fase 4. */
export interface Premio {
  id: number;
  nombre: string;
  descripcion: string | null;
  puntosCosto: number;
}

export interface PremiosResponse {
  premios: Premio[];
}

export interface CanjeResponse {
  ok: boolean;
  saldo: number;
  premio: { id: number; nombre: string };
}

/** Cliente HTTP del marketplace (reemplaza al mock). Ver SPEC-016. */
@Injectable({ providedIn: 'root' })
export class MarketplaceApiService {
  constructor(private http: HttpClient) {}

  getMarcas(rubro?: Rubro | 'todos'): Observable<MarcaResumen[]> {
    const q = rubro && rubro !== 'todos' ? `?rubro=${rubro}` : '';
    return this.http.get<MarcaResumen[]>(`${BASE}/marcas${q}`);
  }

  /** Sedes ordenadas por cercanía, filtradas por rubro y/o servicio. La ubicación no se guarda. */
  getSedesCercanas(p: { lat?: number; lng?: number; rubro?: string; q?: string }): Observable<SedeCercana[]> {
    const qs = new URLSearchParams();
    if (p.lat != null && p.lng != null) {
      // 3 decimales (~100 m): alcanza para ordenar y no manda la ubicación exacta.
      qs.set('lat', p.lat.toFixed(3));
      qs.set('lng', p.lng.toFixed(3));
    }
    if (p.rubro && p.rubro !== 'todos') qs.set('rubro', p.rubro);
    if (p.q?.trim()) qs.set('q', p.q.trim());
    return this.http.get<SedeCercana[]>(`${BASE}/sedes/cercanas?${qs}`);
  }

  getMarca(slug: string): Observable<MarcaDetalle> {
    return this.http.get<MarcaDetalle>(`${BASE}/marca/${slug}`);
  }

  /**
   * Estado operativo de una sede (endpoint anónimo). Las pantallas de marca y sede ya
   * reciben este mismo objeto embebido en cada sede de getMarca(), así que esto se usa
   * sólo para refrescar el dato sin volver a pedir la marca entera.
   */
  getEstadoSede(idSede: number): Observable<EstadoSede> {
    return this.http.get<EstadoSede>(`${BASE}/sede/${idSede}/estado`);
  }

  getAvailability(profesional: number, fecha: string, duracion = 30, idSede?: number): Observable<Slot[]> {
    const sede = idSede ? `&idSede=${idSede}` : '';
    return this.http.get<Slot[]>(
      `${BASE}/booking/availability?profesional=${profesional}&fecha=${fecha}&duracion=${duracion}${sede}`,
    );
  }

  createReserva(input: {
    idServicioPrecio: number;
    idProfesional?: number;
    idSede?: number;
    fechaHoraInicio: string;
  }): Observable<any> {
    // El cliente se toma del JWT (interceptor). No se envía idCliente.
    return this.http.post<any>(`${BASE}/booking`, input);
  }

  /** Cobro con Checkout Bricks: envía el token de tarjeta que devolvió el Brick. */
  payBrick(
    idReserva: number,
    concepto: 'ADELANTO' | 'ABONO',
    formData: any,
  ): Observable<any> {
    return this.http.post<any>(`${BASE}/booking/${idReserva}/pay-brick`, {
      concepto,
      token: formData?.token,
      paymentMethodId: formData?.payment_method_id,
      installments: formData?.installments,
      issuerId: formData?.issuer_id != null ? String(formData.issuer_id) : undefined,
      email: formData?.payer?.email,
    });
  }

  // ---- perfil del cliente (mi cuenta) · SPEC-026 -----------------------------
  /** Citas del cliente logueado (marketplace + clínica). El interceptor adjunta el Bearer. */
  getMisCitas(): Observable<MisCitasResponse> {
    return this.http.get<MisCitasResponse>(`${BASE}/me/citas`);
  }
  /** Recomendaciones que la especialista dejó al cliente logueado. */
  getMisRecomendaciones(): Observable<MisRecomendacionesResponse> {
    return this.http.get<MisRecomendacionesResponse>(`${BASE}/me/recomendaciones`);
  }
  /** Saldo + movimientos de puntos del cliente logueado. SPEC-026 Fase 4. */
  getPuntos(): Observable<PuntosResumen> {
    return this.http.get<PuntosResumen>(`${BASE}/me/puntos`);
  }
  /** Catálogo de premios canjeables (público). */
  getPremios(): Observable<PremiosResponse> {
    return this.http.get<PremiosResponse>(`${BASE}/premios`);
  }
  /** Canjea un premio por puntos (Bearer del marketplace vía interceptor). */
  canjear(idPremio: number): Observable<CanjeResponse> {
    return this.http.post<CanjeResponse>(`${BASE}/me/canjear/${idPremio}`, {});
  }

  // La gestión del negocio (agenda del profesional, alta de empresa, profesionales,
  // servicios, sedes y horarios) NO vive acá: es superficie de proveedor y su cliente
  // es front-alphaC /negocio/* (SPEC-021). Esta app es la de clientes y sólo habla con
  // la superficie public, así que esos métodos se eliminaron — no los usaba ningún
  // componente y quedaban en el bundle público documentando la API privada (SPEC-028).

  /** Color de marca derivado del rubro (el API no lo persiste). */
  /**
   * Acento del negocio: el que manda la API (su color de marca o uno de la paleta de
   * Aura, ya legible sobre blanco; ver api-nest shared/color-marca.ts). Con un backend
   * anterior que no lo manda, el del rubro.
   */
  static colorDe(m: { color?: string | null; rubro: Rubro }): string {
    return m.color && /^#[0-9a-f]{6}$/i.test(m.color) ? m.color : MarketplaceApiService.colorFor(m.rubro);
  }

  static colorFor(rubro: Rubro): string {
    return (
      { spa: '#00A7DB', dentista: '#1E88E5', manicura: '#EC407A', barberia: '#5D4037' } as Record<Rubro, string>
    )[rubro] || '#00A7DB';
  }
}
